import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, Loader, Stack, Text } from '@vipso/shared';
import { useGetScopeQuery } from '../api/scopeApi';
import { useInitAppMutation, useRefreshSessionMutation } from '../api/tenantApi';
import {
  logout,
  setAuthFromInit,
  setBootstrapped,
  setTenantContext,
  type OrgInfo,
} from '../store/authSlice';
import { mapInitAppPayload, type InitAppPayload } from '../store/authUtils';
import { resolveTenantSlug } from '../utils/tenant';
import { resolveTenantDefaultLogoUrl } from '../utils/resolveDisplayLogoUrl';
import type { RootState } from '../store';
import { baseApi } from '../store/baseApi';

function resolveOrgId(org: OrgInfo | null): string | null {
  if (!org) return null;
  if (org.id != null && org.id !== '') return String(org.id);
  if (org.orgId != null && org.orgId !== '') return String(org.orgId);
  return null;
}

const AUTH_ENTRY_PATHS = new Set(['/login', '/otp', '/org-picker', '/boot', '/']);

/**
 * Runs once per full document load (web refresh / native kill-reopen).
 * Always refetches public scope so login branding is fresh — no `/boot` route.
 */
export function AppBootstrap({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const scope = resolveTenantSlug();
  const splashLogoUrl = resolveTenantDefaultLogoUrl(scope);
  const started = useRef(false);

  const bootstrapped = useSelector((state: RootState) => state.auth.bootstrapped);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const refreshToken = useSelector((state: RootState) => state.auth.refreshToken);
  const user = useSelector((state: RootState) => state.auth.user);
  const org = useSelector((state: RootState) => state.auth.org);

  const { data, isLoading, isError, error, isFetching } = useGetScopeQuery(scope, {
    // Every remount (refresh / reopen) must hit the API — do not reuse RTK cache.
    refetchOnMountOrArgChange: true,
  });
  const [initApp] = useInitAppMutation();
  const [refreshSession] = useRefreshSessionMutation();

  useEffect(() => {
    if (!data || started.current) return;
    started.current = true;

    let cancelled = false;

    async function bootstrap() {
      dispatch(
        setTenantContext({
          tenant: data!.tenant,
          variantName: data!.variantName,
          theme: data!.theme,
          config: data!.config,
          tenantOrgs: data!.orgs ?? [],
        }),
      );

      const orgId = resolveOrgId(org);
      const hasSession = Boolean(refreshToken || (accessToken && user?.id && orgId));

      if (!hasSession) {
        dispatch(setBootstrapped(true));
        return;
      }

      try {
        let payload: InitAppPayload | undefined;

        if (refreshToken) {
          const result = await refreshSession({ refreshToken }).unwrap();
          payload = result.refreshSession;
        } else if (user?.id && orgId) {
          const result = await initApp({ userId: user.id, orgId }).unwrap();
          payload = result.initApp;
        }

        if (cancelled || !payload) return;

        dispatch(
          setAuthFromInit(
            mapInitAppPayload(payload, {
              tenant: data!.tenant,
              theme: data!.theme,
              user,
            }),
          ),
        );

        dispatch(baseApi.util.resetApiState());
        dispatch(setBootstrapped(true));

        if (AUTH_ENTRY_PATHS.has(location.pathname)) {
          navigate('/dashboard', { replace: true });
        }
      } catch {
        if (cancelled) return;
        dispatch(logout());
        dispatch(setBootstrapped(true));
        if (location.pathname !== '/login' && location.pathname !== '/otp') {
          navigate('/login', { replace: true });
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
    // Intentionally omit location/navigate from deps — run once per scope payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dispatch, accessToken, refreshToken, user, org, initApp, refreshSession]);

  if (isError) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: '100vh' }} p="md">
        <Alert color="red" title={t('boot.scopeNotFound')}>
          {(error as { data?: { error?: string }; status?: number })?.data?.error ??
            t('boot.scopeLoadFailed', { scope })}
        </Alert>
      </Stack>
    );
  }

  if (!bootstrapped || isLoading || (isFetching && !data)) {
    return (
      <Stack
        align="center"
        justify="center"
        style={{ minHeight: '100vh', background: '#ffffff' }}
        gap="md"
      >
        <img
          src={splashLogoUrl}
          alt=""
          style={{ width: 150, height: 200, objectFit: 'contain' }}
        />
        <Loader />
        <Text c="dimmed" size="sm">
          {isLoading || !data ? t('boot.loadingScope', { scope }) : t('boot.refreshingSession')}
        </Text>
      </Stack>
    );
  }

  return <>{children}</>;
}
